import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Bar, Brush, CartesianGrid, ComposedChart, Legend, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import client from '../api/client';
import CollapsiblePanel from './CollapsiblePanel';
import { ANALYSIS_PERIODS } from './SeedingPreferences';
import './ItemAnalysisSection.css';

// Fixed order so a model keeps its color whichever models are shown
const MODELS = [
  ['MovingAverageModel', 'Moving average', 'var(--series-1)'],
  ['ExponentialSmoothingModel', 'Exponential smoothing', 'var(--series-2)'],
  ['HoltWintersModel', 'Holt-Winters', 'var(--series-3)'],
  ['LinearTrendModel', 'Linear trend', 'var(--series-4)'],
  ['CrostonsModel', "Croston's", 'var(--series-5)'],
  ['TSBModel', 'TSB', 'var(--series-6)'],
  ['SeasonalNaiveModel', 'Seasonal naive', 'var(--series-7)'],
];

const LEGEND_ORDER = ['volume', ...MODELS.map(([key]) => key)];
const byModelOrder = (entry) => LEGEND_ORDER.indexOf(entry.dataKey);
// Days of history in view by default; longer periods stay reachable through the brush
const DEFAULT_VISIBLE_HISTORY = 30;

const compactIsk = new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 2 }).format;
const SYNC_ID = 'item-analysis';

function PriceTooltip({ active, payload, label, formatPrice }) {
  const day = active && payload?.[0]?.payload;
  if (!day || day.average == null) return null;
  return (
    <div className="iar-tooltip">
      <div className="iar-tooltip-label">{shortDate(label)}</div>
      <div>Average {formatPrice(day.average)}</div>
      <div className="iar-tooltip-dim">Low {formatPrice(day.lowest)} · High {formatPrice(day.highest)}</div>
    </div>
  );
}

// One row per day: history days carry volume, forecast days carry each model's value
export function buildRows(data) {
  if (!data) return [];
  const forecast = data.forecast_dates.map((date, i) => ({
    date,
    ...Object.fromEntries(data.models.map((m) => [m.name, m.daily[i]])),
  }));
  return [...data.history, ...forecast];
}

// One numbered marker per patch date; the list below the charts explains each number
export function groupPatchMarkers(patchNotes) {
  const byDate = new Map();
  patchNotes.forEach((note) => {
    if (!byDate.has(note.date)) byDate.set(note.date, []);
    byDate.get(note.date).push(note);
  });
  return [...byDate].map(([date, notes], i) => ({ date, n: i + 1, notes }));
}

// Brush window before the user moves it: the last DEFAULT_VISIBLE_HISTORY days of history plus the forecast
export function defaultView(historyLength, rowCount) {
  return {
    startIndex: historyLength > DEFAULT_VISIBLE_HISTORY ? historyLength - DEFAULT_VISIBLE_HISTORY : 0,
    endIndex: Math.max(0, rowCount - 1),
  };
}

const shortDate = (iso) => new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });

function ItemAnalysisSection({
  destinationId, item, analysisPeriod, setAnalysisPeriod, aggregatePeriod, formatNumber, formatPrice, collapsed, onToggle,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hidden, setHidden] = useState(new Set());
  const [range, setRange] = useState(null); // brush window; null = default
  const sectionRef = useRef(null);
  const itemId = item?.id;

  useEffect(() => {
    if (itemId) sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [itemId]);

  useEffect(() => {
    if (!itemId || collapsed) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    client.get(`/seeding/destinations/${destinationId}/items/${itemId}/forecast-models/`, {
      params: { analysis_period: analysisPeriod },
    })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch((err) => { if (!cancelled) setError(err.response?.data?.error || 'Failed to load forecasts'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // item changes identity when the table refreshes after a preference save, which refetches with saved settings
  }, [destinationId, item, analysisPeriod, collapsed]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = useMemo(() => buildRows(data), [data]);

  useEffect(() => setRange(null), [data]);

  const markers = useMemo(() => groupPatchMarkers(data?.patch_notes || []), [data]);
  const markerLines = (labelled) => markers.map((m) => (
    <ReferenceLine
      key={m.date}
      x={m.date}
      stroke="var(--text-faint)"
      strokeDasharray="4 3"
      label={labelled ? { value: m.n, position: 'top', fill: 'var(--text-dim)', fontSize: 11 } : undefined}
    />
  ));
  const showBrush = data && data.history.length > DEFAULT_VISIBLE_HISTORY;
  const view = range ?? defaultView(data?.history.length ?? 0, rows.length);

  const models = data ? MODELS.filter(([key]) => data.models.some((m) => m.name === key && m.daily.length)) : [];
  const toggleModel = (key) => setHidden((prev) => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  const periodButtons = (
    <div className="iar-periods" role="group" aria-label="Analysis period">
      {ANALYSIS_PERIODS.map((days) => (
        <button
          key={days}
          className={`btn btn-sm ${days === analysisPeriod ? 'btn-primary' : 'btn-ghost'}`}
          disabled={days < aggregatePeriod}
          aria-pressed={days === analysisPeriod}
          onClick={() => setAnalysisPeriod(days)}
        >
          {days}d
        </button>
      ))}
    </div>
  );

  return (
    <div ref={sectionRef} className="iar-root">
      <CollapsiblePanel
        title="Item analysis"
        sub={item ? item.item_name : undefined}
        collapsed={collapsed}
        onToggle={onToggle}
        actions={periodButtons}
      >
        {!item ? (
          <div className="table-empty">Select an item in the table to chart its demand and forecasts.</div>
        ) : error ? (
          <div className="error-message">{error}</div>
        ) : !data ? (
          <div className="table-empty">Loading forecasts…</div>
        ) : (
          <div className={`iar-body${loading ? ' iar-loading' : ''}`}>
            {data.warning && <div className="iar-note">{data.warning}</div>}
            <ResponsiveContainer width="100%" height={showBrush ? 350 : 320}>
              <ComposedChart data={rows} syncId={SYNC_ID} syncMethod="value" margin={{ top: markers.length ? 18 : 8, right: 16, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" vertical={false} />
                <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fill: 'var(--text-dim)', fontSize: 11 }} minTickGap={24} />
                <YAxis tickFormatter={formatNumber} tick={{ fill: 'var(--text-dim)', fontSize: 11 }} width={70} />
                <Tooltip
                  contentStyle={{ backgroundColor: 'var(--surface)', border: '1px solid var(--line-strong)', borderRadius: 6 }}
                  labelStyle={{ color: 'var(--text-dim)' }}
                  itemStyle={{ color: 'var(--text)' }}
                  labelFormatter={shortDate}
                  formatter={(value, name) => [formatNumber(value), name]}
                  itemSorter={byModelOrder}
                />
                <Legend
                  wrapperStyle={{ color: 'var(--text-dim)', fontSize: 12, cursor: 'pointer' }}
                  itemSorter={byModelOrder}
                  onClick={(entry) => entry.dataKey !== 'volume' && toggleModel(entry.dataKey)}
                  formatter={(label, entry) => (
                    <span className={hidden.has(entry.dataKey) ? 'iar-legend-off' : undefined}>{label}</span>
                  )}
                />
                <ReferenceLine
                  x={data.forecast_dates[0]}
                  stroke="var(--line-strong)"
                  label={{ value: 'Forecast', position: 'insideTopLeft', fill: 'var(--text-faint)', fontSize: 11 }}
                />
                {markerLines(true)}
                <Bar dataKey="volume" name="Units sold" fill="var(--text-faint)" radius={[2, 2, 0, 0]} isAnimationActive={false} />
                {models.map(([key, label, color]) => {
                  const selected = key === data.selected_model;
                  return (
                    <Line
                      key={key}
                      dataKey={key}
                      name={selected ? `${label} (chosen)` : label}
                      stroke={color}
                      strokeWidth={selected ? 3 : 2}
                      dot={false}
                      activeDot={{ r: 4 }}
                      hide={hidden.has(key)}
                      connectNulls={false}
                      isAnimationActive={false}
                    />
                  );
                })}
                {showBrush && (
                  <Brush
                    dataKey="date"
                    height={24}
                    startIndex={view.startIndex}
                    endIndex={view.endIndex}
                    onChange={({ startIndex, endIndex }) => setRange({ startIndex, endIndex })}
                    tickFormatter={shortDate}
                    stroke="var(--line-strong)"
                    fill="var(--surface)"
                    travellerWidth={8}
                  />
                )}
              </ComposedChart>
            </ResponsiveContainer>

            <div className="iar-chart-label">Average price</div>
            <ResponsiveContainer width="100%" height={140}>
              {/* Same window as the brush above; tooltips line up by date */}
              <ComposedChart
                data={rows.slice(view.startIndex, view.endIndex + 1)}
                syncId={SYNC_ID}
                syncMethod="value"
                margin={{ top: 4, right: 16, bottom: 0, left: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" vertical={false} />
                <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fill: 'var(--text-dim)', fontSize: 11 }} minTickGap={24} />
                <YAxis
                  tickFormatter={compactIsk}
                  tick={{ fill: 'var(--text-dim)', fontSize: 11 }}
                  width={70}
                  domain={['auto', 'auto']}
                />
                <Tooltip content={<PriceTooltip formatPrice={formatPrice} />} />
                <ReferenceLine x={data.forecast_dates[0]} stroke="var(--line-strong)" />
                {markerLines(false)}
                {/* Empty bar series: gives this axis the same per-day bands as the volume chart so dates line up */}
                <Bar dataKey="band" legendType="none" isAnimationActive={false} />
                <Line dataKey="average" name="Average price" stroke="var(--accent)" strokeWidth={2} dot={false} activeDot={{ r: 4 }} isAnimationActive={false} />
              </ComposedChart>
            </ResponsiveContainer>

            {markers.length > 0 && (
              <ol className="iar-patches" aria-label="Patch notes that change this item">
                {markers.map((m) => m.notes.map((note) => (
                  <li key={`${m.date}-${note.release_id}`}>
                    <span className="iar-patch-n" aria-hidden="true">{m.n}</span>
                    <div>
                      <div className="iar-patch-head">
                        {shortDate(m.date)} · <a href={note.url} target="_blank" rel="noreferrer">{note.title} ({note.release_id})</a> · {note.section}
                      </div>
                      <div className="iar-patch-snippet">{note.snippet}</div>
                    </div>
                  </li>
                )))}
              </ol>
            )}

            <table className="data-table iar-table">
              <thead>
                <tr>
                  <th>Model</th>
                  <th className="num">Next {data.horizon_days} days</th>
                  <th className="num" title="Validation error; lower is better. Seasonal models are scored on a different scale.">Score</th>
                </tr>
              </thead>
              <tbody>
                {models.map(([key, label, color]) => {
                  const m = data.models.find((x) => x.name === key);
                  return (
                    <tr key={key} className={key === data.selected_model ? 'iar-chosen' : undefined}>
                      <td>
                        <span className="iar-swatch" style={{ background: color }} aria-hidden="true" />
                        {label}{key === data.selected_model && <span className="iar-badge">chosen</span>}
                      </td>
                      <td className="num">{formatNumber(m.total)}</td>
                      <td className="num">{m.score == null ? '—' : formatNumber(m.score)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td>Avg. consumption</td>
                  <td className="num">{formatNumber(item.avg_consumption || 0)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
            {!data.selected_model && (
              <div className="iar-note">
                Every model forecast zero or failed, so the forecast of {formatNumber(data.forecast)} uses a recent average instead.
              </div>
            )}
          </div>
        )}
      </CollapsiblePanel>
    </div>
  );
}

export default ItemAnalysisSection;
